/**
 * useCompetitor.ts — State and submit logic for the Competitor Intelligence module.
 */
import { useCallback, useEffect, useState } from 'react'
import { competitor } from '@/api/client'
import type { CompetitorOptions, CompetitorPreset } from '@/types'
import { DEFAULT_COMPETITOR_OPTIONS } from '@/types'
import { useJobPoller } from './useJobPoller'

export function useCompetitor() {
  const [options,  setOptions]  = useState<CompetitorOptions>(DEFAULT_COMPETITOR_OPTIONS)
  const [presets,  setPresets]  = useState<CompetitorPreset[]>([])
  const [presetsLoading, setPresetsLoading] = useState(true)

  const { jobView, beginJob, cancel, reset, setError } = useJobPoller({
    getJob:    competitor.getJob,
    cancelJob: competitor.cancelJob,
  })

  // Load presets on mount
  useEffect(() => {
    competitor.getPresets()
      .then(setPresets)
      .catch(() => {})
      .finally(() => setPresetsLoading(false))
  }, [])

  const submit = useCallback(async () => {
    try {
      const body: Record<string, unknown> = {
        comparison:    options.comparison,
        input_company: options.input_company,
        concurrency:   options.concurrency ?? 4,
      }
      if (options.preset) {
        body.preset = options.preset
      } else {
        body.urls_content    = options.urls_content
        body.features_yaml   = options.features_yaml
      }
      const job = await competitor.createJob(body)
      await beginJob(job.job_id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start competitor analysis.')
    }
  }, [options, beginJob, setError])

  return { options, setOptions, presets, presetsLoading, jobView, submit, cancel, reset }
}
